import "@/instrument"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"
import ReactDOM from "react-dom/client"
import "@common/index.css"

import { client } from "@common/client/client.gen"
import RouteError from "@common/components/shared/route-error"
import { installClientHeaders } from "@common/lib/client-headers"
import { sanitizePostHogEvent } from "@common/lib/posthog"
import { queryClient } from "@common/lib/query-client"
import { installRequestLog } from "@common/lib/request-log"
import { PostHogProvider } from "@posthog/react"
import posthog from "posthog-js"
import { StrictMode } from "react"
import type { LoaderCrumb } from "@/components/layout/layout-breadcrumbs"
import { installVersionCheck } from "@/lib/version-check"
import { routeTree } from "./routeTree.gen"

client.setConfig({
  baseURL: import.meta.env.VITE_API_URL,
  auth: () => localStorage.getItem("access_token") || "",
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  notFoundMode: "root",
  defaultErrorComponent: RouteError,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
  interface StaticDataRouteOption {
    breadcrumb?: string | LoaderCrumb[]
    secondarySidebar?: boolean
    detailSidebar?: boolean
  }
}

// Add TanStack Router integration to Sentry after router is created
if (
  import.meta.env.VITE_SENTRY_DSN_FRONTEND &&
  import.meta.env.VITE_ENVIRONMENT &&
  import.meta.env.VITE_ENVIRONMENT !== "local"
) {
  Sentry.addIntegration(Sentry.tanstackRouterBrowserTracingIntegration(router))
}

function ErrorFallback() {
  return (
    <div>
      <h1>Something went wrong</h1>
      <p>Our team has been notified.</p>
    </div>
  )
}

client.instance.interceptors.request.use((config) => {
  config.headers.set("X-POSTHOG-SESSION-ID", posthog.get_session_id())
  config.headers.set("X-POSTHOG-DISTINCT-ID", posthog.get_distinct_id())
  return config
})

installRequestLog(router)
installClientHeaders("web")
installVersionCheck(router)

window.addEventListener("vite:preloadError", (event) => {
  const key = "vite:preload-reloaded-at"
  const previous = Number(sessionStorage.getItem(key))
  if (Date.now() - previous < 10_000) return
  sessionStorage.setItem(key, String(Date.now()))
  event.preventDefault()
  window.location.reload()
})

const posthog_key = import.meta.env.VITE_PUBLIC_POSTHOG_KEY

if (posthog_key) {
  posthog.init(posthog_key, {
    api_host: `${window.location.origin}/relay`,
    ui_host: "https://eu.posthog.com",
    disable_session_recording: true, // started manually in _layout
    defaults: "2025-11-30",
    before_send: sanitizePostHogEvent,
  })
}

ReactDOM.createRoot(document.getElementById("root")!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </StrictMode>,
)
